import * as React from "react"

interface KeyboardState {
  altKeyPressed: boolean
}

type KeyboardContextInterface = KeyboardState

const KeyboardContext = React.createContext<KeyboardContextInterface | undefined>(undefined)

interface KeyboardContextProviderProps {
  children: React.ReactNode
}

export const KeyboardContextProvider = ({ children }: KeyboardContextProviderProps): JSX.Element => {
  const [state, setState] = React.useState<KeyboardState>({ altKeyPressed: false })

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isAltKey(event)) setState((state) => ({ ...state, altKeyPressed: true }))
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (isAltKey(event)) setState((state) => ({ ...state, altKeyPressed: false }))
    }

    document.body.addEventListener("keydown", handleKeyDown)
    document.body.addEventListener("keyup", handleKeyUp)

    return () => {
      document.body.removeEventListener("keydown", handleKeyDown)
      document.body.removeEventListener("keyup", handleKeyUp)
    }
  }, [])

  const value = React.useMemo(() => ({ initialised: true, ...state }), [state])
  return <KeyboardContext.Provider value={value}>{children}</KeyboardContext.Provider>
}

export function useKeyboardContext(): KeyboardContextInterface {
  const context = React.useContext(KeyboardContext)

  if (context === undefined) {
    throw new Error(`useKeyboardContext must be used within KeyboardContextProvider`)
  }

  return context
}

const isAltKey = (event: KeyboardEvent) => ["AltLeft", "AltRight"].includes(event.code)
